import { type ReactElement, type ReactNode, useReducer } from 'react';

import { type SessionAction, SessionActionType } from './actions';
import { SessionDispatchContext, SessionStateContext } from './contexts';
import { useSessionEffects } from './effects';
import sessionReducer, {
  type SessionState,
  SessionStateStatus,
} from './reducer';
import { lastRealmStore } from './stores';
import { useSynchronizedDispatch } from './utils';

function encodeAction(outgoingAction: SessionAction): SessionAction | null {
  switch (outgoingAction.type) {
    case SessionActionType.RestoreSessionSucceeded:
      return {
        ...outgoingAction,
        // queryClient is not serializable, so we strip it out
        queryClient: null,
      };

    case SessionActionType.UpdateUser:
    case SessionActionType.Logout:
      return outgoingAction;

    default:
      return null;
  }
}

function decodeAction(incomingAction: unknown): SessionAction | null {
  // TODO: actually implement a proper type guard
  return incomingAction as SessionAction;
}

function getInitialSessionState(): SessionState {
  return {
    status: SessionStateStatus.RestoringSession,
    ignoreErrors: true,
    lastRealm: lastRealmStore.unsafe_getState(),
  };
}

export interface SessionManagerProps {
  children?: ReactNode;
}

export default function SessionManager({
  children,
}: SessionManagerProps): ReactElement {
  const [state, rawDispatch] = useReducer(
    sessionReducer,
    undefined,
    getInitialSessionState,
  );

  const dispatch = useSynchronizedDispatch(
    rawDispatch,
    'SONIC_STUDIO',
    encodeAction,
    decodeAction,
  );

  useSessionEffects(state, dispatch);

  return (
    <SessionDispatchContext.Provider value={dispatch}>
      <SessionStateContext.Provider value={state}>
        {children}
      </SessionStateContext.Provider>
    </SessionDispatchContext.Provider>
  );
}
